import { Building2, MessageCircle } from "lucide-react";
import { whatsappSupportUrl, PLATFORM_NAME, PLATFORM_DEVELOPER } from "../lib/platform";

/**
 * Tela institucional exibida quando um tenant está inativo.
 * Preserva a identidade do estabelecimento (logo, nome, segmento) mas NÃO expõe
 * campos de login nem informa motivo (financeiro ou não). Mensagem neutra.
 * Reutilizada por /:slug/login e pelo fluxo /entrar quando o tenant selecionado
 * estiver inativo.
 */
export default function TenantUnavailable({ tenant, testidPrefix = "tenant-unavailable", onBack }) {
  const displayName = tenant?.display_name || tenant?.name || tenant?.slug || "Estabelecimento";
  const logoSrc = tenant?.logo_url || "/logo.png";
  const segment = tenant?.segment;
  const year = new Date().getFullYear();
  const wa = whatsappSupportUrl(
    `Olá, preciso de informações sobre o acesso ao FILA Gestão do estabelecimento ${displayName}.`,
  );
  return (
    <div className="w-full max-w-md" data-testid={testidPrefix}>
      <div className="mb-6 flex flex-col items-center gap-3 text-center">
        {tenant?.logo_url ? (
          <img
            src={logoSrc}
            alt={displayName}
            data-testid={`${testidPrefix}-logo`}
            className="h-24 w-24 sm:h-28 sm:w-28 object-contain rounded-full border-2 border-pink-100 shadow-sm bg-white opacity-80"
            onError={(e) => {
              if (e.currentTarget.dataset.fbk !== "1") {
                e.currentTarget.dataset.fbk = "1";
                e.currentTarget.src = "/logo.png";
              }
            }}
          />
        ) : (
          <div className="flex h-24 w-24 sm:h-28 sm:w-28 items-center justify-center rounded-full border-2 border-pink-100 bg-white text-gray-400">
            <Building2 size={32} />
          </div>
        )}
        <h1
          className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-gray-900"
          data-testid={`${testidPrefix}-name`}
        >
          {displayName}
        </h1>
        {segment && (
          <p className="text-[11px] uppercase tracking-[0.3em] text-pink-600 font-semibold">
            {segment}
          </p>
        )}
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-sm text-center">
        <div className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-[11px] uppercase tracking-widest text-gray-600 mb-4">
          Acesso temporariamente indisponível
        </div>
        <p
          className="text-sm text-gray-600 leading-relaxed"
          data-testid={`${testidPrefix}-message`}
        >
          O acesso deste estabelecimento ao {PLATFORM_NAME} está temporariamente indisponível.
          <br />
          Para mais informações, entre em contato com o suporte.
        </p>
        <a
          href={wa}
          target="_blank"
          rel="noreferrer"
          data-testid={`${testidPrefix}-support-btn`}
          className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-pink-500 px-6 py-3 text-sm font-semibold text-white shadow-sm shadow-pink-500/20 hover:bg-pink-600 hover:-translate-y-[1px] transition-all"
        >
          <MessageCircle size={16} /> Falar com o suporte
        </a>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            data-testid={`${testidPrefix}-back`}
            className="mt-3 block w-full text-xs text-gray-500 hover:text-pink-600"
          >
            Voltar
          </button>
        )}
      </div>

      <p className="mt-6 text-center text-[11px] uppercase tracking-[0.25em] text-gray-400">
        {PLATFORM_NAME} · Desenvolvido por{" "}
        <span className="font-semibold text-gray-600">{PLATFORM_DEVELOPER}</span> · © {year}
      </p>
    </div>
  );
}
